/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * brandTheme.js — Colour maths for the White-Label design system.
 *
 * Converts brand colours (hex, from brand-config.json / admin settings) into
 * the HSL triplets consumed by Tailwind (`hsl(var(--primary))`) and writes
 * them on <html>. Pure functions, no React.
 *
 * Design rules implemented here:
 *  - Light mode uses the brand colour as-is (clamped for AA contrast on white).
 *  - Dark mode automatically LIFTS the lightness so the same brand stays
 *    readable on Zinc-900 (unless `darkPrimaryColor` is provided).
 *  - Foreground (text on primary) is picked by relative luminance.
 */

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Normalises `#abc` / `abc` / `#aabbcc` → `#aabbcc` (lowercase). Returns null when invalid. */
export const normalizeHex = (hex) => {
  if (typeof hex !== 'string') return null;
  const m = hex.trim().match(HEX_RE);
  if (!m) return null;
  let h = m[1].toLowerCase();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return `#${h}`;
};

/** `#rrggbb` → { r, g, b } (0-255). */
export const hexToRgb = (hex) => {
  const n = normalizeHex(hex);
  if (!n) return null;
  const int = parseInt(n.slice(1), 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
};

/** { r, g, b } → { h (0-360), s (0-100), l (0-100) }. */
export const rgbToHsl = ({ r, g, b }) => {
  const rn = r / 255; const gn = g / 255; const bn = b / 255;
  const max = Math.max(rn, gn, bn); const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0; let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = ((gn - bn) / d + (gn < bn ? 6 : 0)); break;
      case gn: h = ((bn - rn) / d + 2); break;
      default: h = ((rn - gn) / d + 4);
    }
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
};

/** Hex → Tailwind-ready HSL triplet string, e.g. "175 77% 26%". */
export const hexToHslTriplet = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { h, s, l } = rgbToHsl(rgb);
  return `${h} ${s}% ${l}%`;
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const triplet = ({ h, s, l }) => `${h} ${s}% ${l}%`;

/** WCAG relative luminance (0 = black, 1 = white). */
export const relativeLuminance = ({ r, g, b }) => {
  const lin = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

/**
 * Builds the full set of CSS variables for a brand palette.
 * @param {object} brand   { primaryColor, secondaryColor|primaryColorAlt, darkPrimaryColor }
 * @returns {{ light: Record<string,string>, dark: Record<string,string> }}
 */
export const buildBrandCssVars = (brand = {}) => {
  const primaryRgb = hexToRgb(brand.primaryColor);
  const secondaryHex = brand.secondaryColor || brand.primaryColorAlt;
  const secondaryRgb = hexToRgb(secondaryHex);
  const darkPrimaryRgb = hexToRgb(brand.darkPrimaryColor);

  const light = {};
  const dark = {};

  if (primaryRgb) {
    const hsl = rgbToHsl(primaryRgb);
    // Light: clamp lightness so white text stays AA on the button.
    const lightHsl = { ...hsl, l: clamp(hsl.l, 18, 48) };
    light['--primary'] = triplet(lightHsl);
    light['--ring'] = triplet(lightHsl);
    light['--primary-foreground'] = '0 0% 100%';

    // Dark: lift lightness for contrast on zinc-900, dark text on top.
    const darkHsl = darkPrimaryRgb
      ? rgbToHsl(darkPrimaryRgb)
      : { ...hsl, s: clamp(hsl.s - 8, 30, 90), l: clamp(hsl.l + 24, 48, 66) };
    dark['--primary'] = triplet(darkHsl);
    dark['--ring'] = triplet(darkHsl);
    const darkRgbForLum = darkPrimaryRgb || hslToRgb(darkHsl);
    dark['--primary-foreground'] = relativeLuminance(darkRgbForLum) > 0.35 ? '240 6% 10%' : '0 0% 100%';
  }

  if (secondaryRgb) {
    const hsl = rgbToHsl(secondaryRgb);
    const lightHsl = { ...hsl, l: clamp(hsl.l, 25, 50) };
    light['--accent'] = triplet(lightHsl);
    light['--primary-alt'] = triplet(lightHsl);
    light['--accent-foreground'] = '0 0% 100%';
    const darkHsl = { ...hsl, l: clamp(hsl.l + 12, 45, 62) };
    dark['--accent'] = triplet(darkHsl);
    dark['--primary-alt'] = triplet(darkHsl);
    dark['--accent-foreground'] = '240 6% 10%';
  }

  return { light, dark };
};

/** HSL (0-360, 0-100, 0-100) → RGB (0-255). */
export const hslToRgb = ({ h, s, l }) => {
  const sn = s / 100; const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = ln - c / 2;
  let r = 0; let g = 0; let b = 0;
  if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; } else if (h < 180) { g = c; b = x; } else if (h < 240) { g = x; b = c; } else if (h < 300) { r = x; b = c; } else { r = c; b = x; }
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
};

const STYLE_TAG_ID = 'brand-theme-vars';

/**
 * Injects (or replaces) a <style> tag with the computed brand variables for
 * both `:root` and `.dark`. Using a style tag (rather than inline
 * `element.style`) lets the same brand colour resolve differently per theme
 * and keeps the cascade predictable (specificity == design tokens in CSS).
 */
export const applyBrandTheme = (brand) => {
  if (typeof document === 'undefined') return;
  const { light, dark } = buildBrandCssVars(brand);
  const toCss = (vars) => Object.entries(vars).map(([k, v]) => `${k}: ${v};`).join(' ');
  const css = `:root { ${toCss(light)} } .dark { ${toCss(dark)} }`;
  let tag = document.getElementById(STYLE_TAG_ID);
  if (!tag) {
    tag = document.createElement('style');
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = css;

  // Legacy raw hex variables (used by charts and a few components)
  const root = document.documentElement;
  if (brand.primaryColor) root.style.setProperty('--wl-primary', brand.primaryColor);
  const secondary = brand.secondaryColor || brand.primaryColorAlt;
  if (secondary) root.style.setProperty('--wl-primary-alt', secondary);
  if (brand.accentColor) root.style.setProperty('--wl-accent', brand.accentColor);
};
